import { useMemo, useState } from 'react';
import { togglePrimitiveItemInArray } from 'utils/helpers';
import { ModalManager } from 'context/ModalRouter';
import { usePost } from 'modules/post/postSelector';
import PostApi, { PostScheduleValidation, ScheduleParams } from 'api/post';
import { queryKey } from 'constants/queryKey';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { PostApprovalWorkflowPermissions } from 'utils/post/permissions';
import { useUser } from 'modules/user/userSelector';
import UserpilotUtils from 'app/utils/userpilot';
import { useG2ReviewTrigger } from 'app/hooks/useG2ReviewTrigger';
import { filterPostsByStatusSelection } from 'app/modules/posts/modules/timeSavers/utils/filterPostsByStatusSelection';
import { useToast } from 'app/hooks/useToast';
import { ListPost, PostDetail } from 'types/PostDetail';
import { useTranslation } from 'react-i18next';
import { PostStatus } from '@kontentino/kontentino-constants/Posts';

type Props = {
  initialPostsIds?: number[];
  modalManager: ModalManager;
};

function useSchedulePostsContainer(props: Props) {
  const { availableStatuses } = useUser();
  const toast = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { posts } = usePost();
  const { triggerG2Review } = useG2ReviewTrigger();
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);
  const filteredPostsByStatuses = filterPostsByStatusSelection({
    allowedStatuses: getAllowedStatuses(),
    selectedStatuses: selectedStatuses,
    posts,
  });
  const [selectedPosts, setSelectedPosts] = useState<ListPost[]>(
    filteredPostsByStatuses.filter((post) =>
      props.initialPostsIds?.includes(post.id),
    ),
  );

  const validation = useQuery(
    [queryKey.multipleScheduleValidations, filteredPostsByStatuses],
    () => PostApi.validate(filteredPostsByStatuses.map((post) => post.id)),
    { onSuccess: unSelectInvalidPosts, refetchOnWindowFocus: false },
  );
  const schedulePosts = useMutation(
    (chosenMethod: ScheduleParams['chosenMethod']) =>
      PostApi.schedule({
        ids: selectedPosts.map((selectedPost) => selectedPost.id),
        chosenMethod,
      }),
    {
      onSuccess: () => {
        UserpilotUtils.track(UserpilotUtils.events.POST_SCHEDULED);
        triggerG2Review();
      },
    },
  );

  function togglePostStatus(status: number) {
    setSelectedStatuses((prevSelectedStatuses) => {
      return togglePrimitiveItemInArray<number>(status, prevSelectedStatuses);
    });
  }

  function unSelectInvalidPosts(validation: PostScheduleValidation[]) {
    setSelectedPosts((posts) => {
      const newPosts = posts.filter((post) =>
        getIsPostValid(post.id, validation),
      );

      if (newPosts.length < posts.length) {
        toast(
          'Some posts were unselected due to missing scheduling requirements. Please review your posts and all the required information.',
          'warning',
        );
      }

      return newPosts;
    });
  }

  function togglePost(post: ListPost) {
    if (!validation.data) {
      toast(t('postValidationInProcess'), 'warning');
      return;
    }

    if (getIsPostValid(post.id, validation.data)) {
      setSelectedPosts((posts) => {
        if (posts.map((selectedPost) => selectedPost.id).includes(post.id)) {
          return posts.filter((selectedPost) => selectedPost.id !== post.id);
        } else {
          return [...posts, post];
        }
      });
    }
  }

  function selectAllPosts() {
    if (!validation.data) {
      toast(t('postValidationInProcess'), 'warning');
      return;
    }

    const postsToSelect = filteredPostsByStatuses.filter((post) =>
      getIsPostValid(post.id, validation.data),
    );

    setSelectedPosts(postsToSelect);
  }

  function unselectAllPosts() {
    setSelectedPosts([]);
  }

  function getIsPostValid(
    postId: PostDetail['id'],
    validationResponse: PostScheduleValidation[],
  ) {
    const postValidation = validationResponse.find(
      (validation) => validation.postId === postId,
    );

    const canDoAtLeastOne =
      !!postValidation &&
      (postValidation.canExport ||
        postValidation.canSchedule ||
        postValidation.canPublishNow ||
        postValidation.canPublishDarkPost);

    return canDoAtLeastOne;
  }

  function getAllowedStatuses() {
    return availableStatuses(posts.map((post) => post.page.type)).filter(
      (status) => {
        return PostApprovalWorkflowPermissions(status as PostStatus)
          .canSchedule;
      },
    );
  }

  function closeModal() {
    queryClient.invalidateQueries(queryKey.posts());

    props.modalManager.close();
  }

  const disabledPosts = useMemo(() => {
    return (validation.data ?? [])
      .filter((value) => !getIsPostValid(value.postId, validation.data ?? []))
      .map((value) => value.postId);
  }, [validation.data]);

  const canExportInAdvance = useMemo(() => {
    return selectedPosts
      .map((post) => post.id)
      .every(
        (postId) =>
          validation?.data?.find((value) => value.postId === postId)?.canExport,
      );
  }, [validation.data, selectedPosts]);

  const canPublishNow = useMemo(() => {
    return selectedPosts
      .map((post) => post.id)
      .every(
        (postId) =>
          validation?.data?.find((value) => value.postId === postId)
            ?.canPublishNow,
      );
  }, [validation.data, selectedPosts]);

  const canSchedule = useMemo(() => {
    return selectedPosts
      .map((post) => post.id)
      .every(
        (postId) =>
          validation?.data?.find((value) => value.postId === postId)
            ?.canSchedule,
      );
  }, [validation.data, selectedPosts]);

  const invalidPostsMessages = useMemo(() => {
    const messages: Record<number, string> = {};

    if (validation.data) {
      validation.data.forEach((value) => {
        if (!!value.messages.errors.length) {
          messages[value.postId] = value.messages.errors[0].message;
        }
      });
    }

    return messages;
  }, [validation.data]);

  return {
    selectedPosts,
    selectedStatuses,
    togglePostStatus,
    togglePost,
    selectAllPosts,
    unselectAllPosts,
    closeModal,
    isLoadingValidation: validation.isLoading,
    canExportInAdvance,
    canPublishNow,
    canSchedule,
    invalidPostsMessages,
    disabledPosts,
    isScheduling: schedulePosts.isLoading,
    onSubmit: schedulePosts.mutate,
    scheduleResponse: schedulePosts.data,
    scheduleChosenMethod: schedulePosts.variables,
    posts: filteredPostsByStatuses,
    allowedStatuses: getAllowedStatuses(),
  };
}

export default useSchedulePostsContainer;
